/**
 * 位置情報ユーティリティ(ジオフェンス判定・徒歩所要時間推定)
 */

export interface LatLng {
  lat: number;
  lng: number;
}

const EARTH_RADIUS_M = 6371008.8;

/** 2地点間の距離(メートル)。ハバサイン公式。 */
export function distanceMeters(a: LatLng, b: LatLng): number {
  const toRad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * toRad;
  const dLng = (b.lng - a.lng) * toRad;
  const lat1 = a.lat * toRad;
  const lat2 = b.lat * toRad;
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * ジオフェンス判定。
 * GPS 誤差(accuracy)を許容側に加算する。誤差が極端に大きい測位
 * (例: Wi-Fi 測位で 1km)は信用できないため上限を設ける。
 */
export const MAX_TRUSTED_ACCURACY_M = 200;

export function withinGeofence(
  pos: LatLng & { acc?: number | null },
  target: LatLng,
  radiusM: number,
): boolean {
  const acc = Math.min(Math.max(pos.acc ?? 0, 0), MAX_TRUSTED_ACCURACY_M);
  return distanceMeters(pos, target) <= radiusM + acc;
}

/**
 * 座標から徒歩所要時間の下限(分)を推定する。
 *
 * checkpoints.json の min_walk_minutes が未設定のCP間で使うフォールバック。
 * 時系列妥当性チェック(CLAUDE.md §9-3)は「これを下回ったらフラグ」なので、
 * 誤検知を避けるため意図的に短めに見積もる:
 *   - 直線距離 × 1.3(道の迂回係数)
 *   - 100 m/分(かなり速い早歩き。高齢者の実際の歩行速度 60-70 m/分より速い)
 * 現地調査後に実測値で min_walk_minutes を上書きすること。
 */
export function estimateMinWalkMinutes(a: LatLng, b: LatLng): number {
  const detourM = distanceMeters(a, b) * 1.3;
  return Math.max(1, Math.round(detourM / 100));
}
